// Generated by GitHub Copilot
// US1794533: Application Name on Impact Report Form
// Async searchable dropdown for workload selection

import React, { useState, useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import { workloadService } from "../../services/workloadService";

/**
 * AsyncWorkloadSelect Component
 * Searchable dropdown that searches workloads as user types
 * Includes debouncing to reduce API calls
 */
function AsyncWorkloadSelect({ value, onChange, isInvalid, disabled }) {
  const [isLoading, setIsLoading] = useState(false);

  // Debounce timer reference (persistent across renders)
  const debounceTimer = useRef(null);

  // Load options from API with debouncing
  const loadOptions = useCallback((inputValue, callback) => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // If input is empty, return empty results
    if (!inputValue || inputValue.trim() === "") {
      callback([]);
      return;
    }

    // Set new timer for debouncing (500ms)
    debounceTimer.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const workloads = await workloadService.searchWorkloadsByName(
          inputValue,
          50
        );

        // Transform workloads to react-select format
        const options = workloads.map((workload) => ({
          value: workload.CIID, // Use CI ID as value
          label: workload.Name, // Display name
          data: workload, // Keep full workload data for later use
        }));

        callback(options);
      } catch (error) {
        console.error("Failed to load workloads:", error);
        callback([]);
      } finally {
        setIsLoading(false);
      }
    }, 500); // 500ms debounce delay
  }, []);

  // Handle selection change
  const handleChange = (selectedOption) => {
    if (selectedOption) {
      // Call onChange with both the name and the CI ID
      onChange({
        applicationName: selectedOption.label,
        ci: selectedOption.value,
        fullData: selectedOption.data,
      });
    } else {
      // Clear selection
      onChange({
        applicationName: "",
        ci: "",
        fullData: null,
      });
    }
  };

  // Convert current value to react-select format
  const selectValue = value
    ? {
        value: value,
        label: value,
      }
    : null;

  return (
    <AsyncSelect
      classNamePrefix="rs"
      cacheOptions
      loadOptions={loadOptions}
      onChange={handleChange}
      value={selectValue}
      placeholder="Start typing to search workloads..."
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No workloads found matching "${inputValue}"`
          : "Start typing to search"
      }
      loadingMessage={() => "Searching workloads..."}
      isLoading={isLoading}
      isClearable
      isDisabled={disabled}
      className={isInvalid ? "is-invalid" : ""}
      styles={{
        control: (base, state) => ({
          ...base,
          borderColor: isInvalid
            ? "#dc3545"
            : state.isFocused
            ? "#80bdff"
            : base.borderColor,
          boxShadow: isInvalid
            ? "0 0 0 0.2rem rgba(220, 53, 69, 0.25)"
            : state.isFocused
            ? "0 0 0 0.2rem rgba(0, 123, 255, 0.25)"
            : base.boxShadow,
          "&:hover": {
            borderColor: isInvalid ? "#dc3545" : "#80bdff",
          },
        }),
      }}
    />
  );
}

export default AsyncWorkloadSelect;
